import { create } from 'zustand';
import { getTenantId } from './authStore';

const LOGIC_API = import.meta.env.VITE_LOGIC_SERVICE_URL || 'http://localhost:8012';

export interface InputField {
  name: string;
  type: string;
  description?: string;
  object_type?: string;
  required: boolean;
}

export interface FilterRow {
  field: string;
  op: string;
  value: string;
}

// Tag carried by blocks the Logic Copilot proposed but the user hasn't
// accepted yet. Stripped on Accept; the block (or the patch) is reverted on
// Reject. Save is blocked while any block still carries one.
export interface ProposalTag {
  action: 'add' | 'update' | 'remove';
  reasoning?: string;
  // For 'update': the changed keys' previous values, to restore on Reject.
  previous?: Record<string, unknown>;
}

export interface Block {
  id: string;
  type: 'ontology_query' | 'connector_query' | 'llm_call' | 'action' | 'ontology_update' | 'ontology_create' | 'transform' | 'send_email' | 'utility_call' | 'conditional' | 'foreach' | 'sharepoint_extract_facturas' | 'document_extract' | 'file_upload' | 'code' | 'delay' | 'logic_call' | 'agent_call' | 'semantic_search' | 'http_call' | 'analysis_board';
  label?: string;
  _proposal?: ProposalTag;
  // ontology_query
  config?: Record<string, unknown>; // includes object_type, filters: FilterRow[], limit
  // send_email
  to?: string;
  subject?: string;
  body?: string;
  from_name?: string;
  bcc?: string;
  // llm_call
  prompt_template?: string;
  system_prompt?: string;
  model?: string;
  output_schema?: Record<string, unknown>;
  max_tokens?: number;
  // action
  action_name?: string;
  params?: Record<string, unknown>;
  reasoning?: string;
  // transform
  operation?: string;
  source?: string;
  field?: string;
  value?: string;
  template?: string;
  // transform.map_fields
  mappings?: Record<string, string>;
  keep_unmapped?: boolean;
  // utility_call
  utility_id?: string;
  utility_params?: Record<string, string>;
  // conditional
  condition_expression?: string;
  true_branch?: string[];   // block ids to run when true
  false_branch?: string[];  // block ids to run when false
  // foreach
  array_input?: string;
  iteration_variable?: string;
  loop_blocks?: string[];   // block ids inside the loop
  // delay / logic_call / agent_call / ontology_create live under `config`
  // (all new block types are config-nested)
}

/** One finding of the save-time checks (backend validation.py). */
export interface LogicIssue {
  severity: 'error' | 'warning';
  message: string;
  block_id?: string;
  field?: string;
  /** Stable id the editor translates (code_syntax, ref_unknown, ...). */
  code?: string;
  params?: Record<string, unknown>;
}

export interface LogicChecks {
  valid: boolean;
  error_count: number;
  warning_count: number;
  issues?: LogicIssue[];
  ontology_checked?: boolean;
  checked_at?: string;
}

/** An entry of a function's history: every create, save, publish and restore. */
export interface LogicFunctionVersion {
  id: string;
  function_id: string;
  version: number;
  kind: 'create' | 'save' | 'publish' | 'restore';
  name?: string;
  message?: string | null;
  author?: string | null;
  restored_from?: number | null;
  block_count: number;
  checks?: LogicChecks | null;
  created_at?: string;
  // Only on GET /versions/{id}
  description?: string;
  input_schema?: InputField[];
  blocks?: Block[];
  output_block?: string;
}

export interface LogicFunction {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  input_schema: InputField[];
  blocks: Block[];
  output_block?: string;
  version: number;
  status: string;
  published_version?: number;
  /** The draft moved past the published snapshot; callers still run the published version. */
  has_unpublished_changes?: boolean;
  /** Checks run on the last save / publish / restore. */
  checks?: LogicChecks | null;
  created_at?: string;
  updated_at?: string;
}

export interface BlockTrace {
  result: unknown;
  duration_ms: number;
  status: string;
  error?: string;
}

export interface LogicRun {
  id: string;
  function_id: string;
  function_version: number;
  inputs: Record<string, unknown>;
  status: string;
  trace?: Record<string, BlockTrace>;
  output?: unknown;
  error?: string;
  triggered_by?: string;
  started_at?: string;
  finished_at?: string;
  created_at?: string;
  // True when the run executed with writes simulated (no side effects).
  dry_run?: boolean;
}

interface LogicStore {
  functions: LogicFunction[];
  selectedFn: LogicFunction | null;
  runs: LogicRun[];
  lastRun: LogicRun | null;
  loading: boolean;
  running: boolean;
  // A prompt queued for the Logic Copilot from OUTSIDE Logic Studio (e.g. the
  // global assistant's create_logic action). The Studio consumes it on open:
  // the Copilot panel auto-sends it and clears this.
  pendingCopilotSeed: { prompt: string } | null;
  setPendingCopilotSeed: (seed: { prompt: string } | null) => void;

  fetchFunctions: () => Promise<void>;
  selectFunction: (fn: LogicFunction | null) => void;
  createFunction: (data: Partial<LogicFunction>) => Promise<LogicFunction>;
  updateFunction: (id: string, data: Partial<LogicFunction> & { message?: string; expected_version?: number }) => Promise<void>;
  deleteFunction: (id: string) => Promise<void>;
  publishFunction: (id: string) => Promise<void>;
  runSync: (id: string, inputs: Record<string, unknown>, dryRun?: boolean, draft?: LogicDraftOverride) => Promise<LogicRun>;
  fetchRuns: (functionId: string) => Promise<void>;
}

/** An unsaved draft a dry run rehearses instead of the stored function. */
export interface LogicDraftOverride {
  blocks: LogicFunction['blocks'];
  input_schema: LogicFunction['input_schema'];
  output_block?: string;
}

/** A save refused because the function changed elsewhere (HTTP 409). */
export class LogicVersionConflictError extends Error {
  code = 'version_conflict' as const;
  currentVersion?: number;
  constructor(message: string, currentVersion?: number) {
    super(message);
    this.name = 'LogicVersionConflictError';
    this.currentVersion = currentVersion;
  }
}

export const useLogicStore = create<LogicStore>((set, get) => ({
  functions: [],
  selectedFn: null,
  runs: [],
  lastRun: null,
  loading: false,
  running: false,
  pendingCopilotSeed: null,
  setPendingCopilotSeed: (seed) => set({ pendingCopilotSeed: seed }),

  fetchFunctions: async () => {
    set({ loading: true });
    try {
      const r = await fetch(`${LOGIC_API}/logic/functions`, {
        headers: { 'x-tenant-id': getTenantId() },
      });
      const data = await r.json();
      set({ functions: Array.isArray(data) ? data : [] });
    } finally {
      set({ loading: false });
    }
  },

  selectFunction: (fn) => set({ selectedFn: fn }),

  createFunction: async (data) => {
    const r = await fetch(`${LOGIC_API}/logic/functions`, {
      method: 'POST',
      headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!r.ok) {
      // Don't replace state with an error envelope. Let the caller decide.
      let detail = `${r.status} ${r.statusText}`;
      try { const e = await r.json(); detail = e.detail || JSON.stringify(e); } catch { /* keep status */ }
      throw new Error(`Logic function create failed: ${detail}`);
    }
    const fn = await r.json();
    set((s) => ({ functions: [fn, ...s.functions], selectedFn: fn }));
    return fn;
  },

  updateFunction: async (id, data) => {
    const r = await fetch(`${LOGIC_API}/logic/functions/${id}`, {
      method: 'PUT',
      headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!r.ok) {
      // Don't merge an error envelope into state — that's how `fn.status`
      // becomes undefined and the sidebar crashes on .toUpperCase().
      let detail = `${r.status} ${r.statusText}`;
      try {
        const e = await r.json();
        if (r.status === 409 && e?.detail?.code === 'version_conflict') {
          throw new LogicVersionConflictError(String(e.detail.message || detail), e.detail.current_version);
        }
        detail = e.detail || JSON.stringify(e);
      } catch (err) {
        if (err instanceof LogicVersionConflictError) throw err;
        /* keep status */
      }
      throw new Error(`Logic function save failed: ${detail}`);
    }
    const fn = await r.json();
    set((s) => ({
      functions: s.functions.map((f) => (f.id === id ? fn : f)),
      selectedFn: s.selectedFn?.id === id ? fn : s.selectedFn,
    }));
  },

  deleteFunction: async (id) => {
    await fetch(`${LOGIC_API}/logic/functions/${id}`, {
      method: 'DELETE',
      headers: { 'x-tenant-id': getTenantId() },
    });
    set((s) => ({
      functions: s.functions.filter((f) => f.id !== id),
      selectedFn: s.selectedFn?.id === id ? null : s.selectedFn,
    }));
  },

  publishFunction: async (id) => {
    const r = await fetch(`${LOGIC_API}/logic/functions/${id}/publish`, {
      method: 'POST',
      headers: { 'x-tenant-id': getTenantId() },
    });
    const fn = await r.json();
    set((s) => ({
      functions: s.functions.map((f) => (f.id === id ? fn : f)),
      selectedFn: s.selectedFn?.id === id ? fn : s.selectedFn,
    }));
  },

  runSync: async (id, inputs, dryRun = false, draft) => {
    set({ running: true, lastRun: null });
    try {
      // The editor runs the draft it shows (?draft=true); callers outside
      // the editor get the published snapshot.
      const r = await fetch(`${LOGIC_API}/logic/functions/${id}/run/sync${dryRun ? '?dry_run=true' : '?draft=true'}`, {
        method: 'POST',
        headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ inputs, triggered_by: 'studio', ...(dryRun && draft ? { draft_override: draft } : {}) }),
      });
      const run = await r.json();
      set({ lastRun: run });
      return run;
    } finally {
      set({ running: false });
    }
  },

  fetchRuns: async (functionId) => {
    const r = await fetch(`${LOGIC_API}/logic/runs?function_id=${functionId}&limit=20`, {
      headers: { 'x-tenant-id': getTenantId() },
    });
    const data = await r.json();
    set({ runs: Array.isArray(data) ? data : [] });
  },
}));
